import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Grid from '@mui/material/Grid2';
import type { RootState } from '../../../store';
import {
  useAdminListarAvaliacoesQuery,
  useAdminRemoverAvaliacaoMutation,
  type AvaliacaoAdminItem,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';
import { DialogConfirmar } from '../../../components/admin/dialog-confirmar';
import { SnackbarFeedback, useFeedback } from '../../../components/admin/feedback';
import { PaginacaoTabela, useUltimaPagina } from '../../../components/admin/paginacao-tabela';

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function avatarUrl(seed: string): string {
  return `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(seed)}`;
}

export function AdminAvaliacoesPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const [pagina, setPagina] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const { data, isLoading } = useAdminListarAvaliacoesQuery({ idConfig, pagina: pagina + 1, tamanhoPagina });
  const { itens: avaliacoes, total } = useUltimaPagina(data);
  const [remover, statusRemover] = useAdminRemoverAvaliacaoMutation();
  const { feedback, mostrarSucesso, mostrarErro, fecharFeedback } = useFeedback();
  /** Avaliação aguardando confirmação de ocultação — `null` = nenhuma. */
  const [ocultandoId, setOcultandoId] = useState<number | null>(null);

  // Estatísticas vêm agregadas do backend (sobre TODAS as avaliações, não só a página).
  const estatisticas = {
    total,
    media: data?.estatisticas?.media ?? 0,
    distribuicao: data?.estatisticas?.distribuicao ?? { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  };

  async function ocultar(id: number): Promise<void> {
    try {
      await remover(id).unwrap();
      mostrarSucesso('Avaliação ocultada');
    } catch {
      mostrarErro('Não foi possível ocultar a avaliação. Tente de novo.');
    }
  }

  return (
    <>
      <HeaderTela
        titulo="Avaliações"
        descricao="Feedback dos clientes após o pedido. Você pode ocultar avaliações inadequadas — elas continuam no banco, só não aparecem aqui."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Stack spacing={0.5}>
              <Typography variant="overline" color="text.secondary">
                Total de avaliações
              </Typography>
              <Typography variant="h4" component="div">
                {estatisticas.total}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <Stack spacing={0.5}>
              <Typography variant="overline" color="text.secondary">
                Média de estrelas
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4" component="div">
                  {estatisticas.total === 0 ? '—' : estatisticas.media.toFixed(1)}
                </Typography>
                {estatisticas.total > 0 && (
                  <Rating value={estatisticas.media} precision={0.1} readOnly />
                )}
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 6 }}>
            <Stack spacing={0.5}>
              <Typography variant="overline" color="text.secondary">
                Distribuição
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                rowGap={1}
              >
                {([5, 4, 3, 2, 1] as const).map((n) => (
                  <Chip
                    key={n}
                    size="small"
                    variant="outlined"
                    label={`${n}★: ${estatisticas.distribuicao[n]}`}
                  />
                ))}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined">
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : avaliacoes.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Nenhuma avaliação ainda — o link de avaliação é enviado por e-mail quando o pedido fica
              pronto.
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />}>
            {avaliacoes.map((item) => (
              <LinhaAvaliacao
                key={item.avaliacao.id}
                item={item}
                aoOcultar={() => setOcultandoId(item.avaliacao.id)}
                ocultando={statusRemover.isLoading}
              />
            ))}
          </Stack>
        )}
        <PaginacaoTabela
          total={total}
          pagina={pagina}
          tamanhoPagina={tamanhoPagina}
          aoMudarPagina={setPagina}
          aoMudarTamanho={(t) => {
            setTamanhoPagina(t);
            setPagina(0);
          }}
        />
      </Paper>

      <DialogConfirmar
        aberto={ocultandoId !== null}
        titulo="Ocultar esta avaliação?"
        descricao="Ela continua registrada no banco, mas deixa de aparecer aqui e para os clientes."
        textoConfirmar="Ocultar"
        processando={statusRemover.isLoading}
        aoCancelar={() => setOcultandoId(null)}
        aoConfirmar={() => {
          const id = ocultandoId;
          setOcultandoId(null);
          if (id != null) void ocultar(id);
        }}
      />

      <SnackbarFeedback feedback={feedback} aoFechar={fecharFeedback} />
    </>
  );
}

interface LinhaAvaliacaoProps {
  item: AvaliacaoAdminItem;
  aoOcultar: () => void;
  ocultando: boolean;
}

function LinhaAvaliacao({ item, aoOcultar, ocultando }: LinhaAvaliacaoProps): JSX.Element {
  const { avaliacao, pedido } = item;
  const dataFormatada = formatadorData.format(new Date(avaliacao.dataAvaliacao));

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 'auto' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={avatarUrl(pedido.clienteNome || `pedido-${pedido.id}`)}
              alt={pedido.clienteNome ?? undefined}
              sx={{ width: 48, height: 48, bgcolor: 'background.default' }}
            />
            <Stack spacing={0.25}>
              <Typography variant="subtitle2">{pedido.clienteNome || 'Cliente anônimo'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {dataFormatada}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 'grow' }}>
          <Stack spacing={1}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Rating value={avaliacao.estrelas} readOnly />
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" variant="outlined" label={pedido.situacao} />
                <Typography
                  component="a"
                  href={`#/admin/pedidos/${pedido.id}`}
                  variant="body2"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  Pedido #{pedido.shortId ?? pedido.id.slice(0, 8).toUpperCase()}
                </Typography>
              </Stack>
            </Stack>
            {avaliacao.comentario && (
              <Box
                component="blockquote"
                sx={{
                  m: 0,
                  pl: 2,
                  borderLeft: '3px solid',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                <Typography variant="body2" component="p" sx={{ m: 0 }}>
                  {avaliacao.comentario}
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'center' } }}>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<VisibilityOffIcon />}
              onClick={aoOcultar}
              disabled={ocultando}
              fullWidth
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              Ocultar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
